import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { SkipNavLink, SkipNavContent } from '@reach/skip-nav';
import '@reach/skip-nav/styles.css';

import { useHeaderFooter } from '@hooks/useHeaderFooter';
import { useTabNavigation } from '@hooks/useTabNavigation';
import { getTaxonomyLabels } from '@utils';
import {
  Region,
  RegionsStore,
  REGION_STATE,
} from '@common/components/Region/Region';
import MainNavigation from '@components/navigations/MainNavigation/MainNavigation';
import { Global } from '@common/components/Global/Global';
import { Disclaimers } from '@common/components/Paragraph/components/Disclaimers/Disclaimers';
import FooterComponent from '@shcc/components/FooterComponent/FooterComponent';
import isTouch from '@shcc/utils/helpers/isTouch';

export const REGION_TYPE = {
  page: 'Region/Page',
  overlay: 'Region/Overlay',
};

export const LAYOUT_TYPE = {
  main: 'Layout/Type/Main',
};

import styles from './MainLayout.module.scss';

export const MainLayout = ({ children, disclaimers, dispatch, dir, type }) => {
  const labelsHeader = getTaxonomyLabels('header')['skip-to-main-content'];
  const labelsDisclaimer =
    getTaxonomyLabels('disclaimers')['section-disclaimer-title'];

  const { copyrightBlock, privacyBlock, socialLinksBlock, getMenuLinks } =
    useHeaderFooter();

  const isUsingTab = useTabNavigation();

  useEffect(() => {
    if (isUsingTab) {
      document.body.classList.add('is-using-tab');
    } else {
      document.body.classList.remove('is-using-tab');
    }
  }, [isUsingTab]);

  return (
    <RegionsStore>
      <Global />
      <SkipNavLink>{labelsHeader}</SkipNavLink>
      <MainNavigation type={type} />
      <Region
        className={styles.regionPages}
        name={REGION_TYPE.page}
        damping={isTouch() ? 0 : 0.095}
        state={REGION_STATE.active}
      >
        <SkipNavContent />
        <main dir={dir}>{children}</main>
        <FooterComponent
          linksFirst={getMenuLinks('footer-first-en')}
          linksSecond={getMenuLinks('footer-second-en')}
          linksSocial={socialLinksBlock}
          copyrightBlock={copyrightBlock}
          privacyBlock={privacyBlock}
        />
        {disclaimers && (
          <Disclaimers
            title={labelsDisclaimer}
            paragraph={disclaimers}
            dispatch={dispatch}
          />
        )}
      </Region>
      <Region
        className={styles.regionOverlays}
        name={REGION_TYPE.overlay}
        damping={isTouch() ? 0 : 0.095}
        state={REGION_STATE.inactive}
      />
    </RegionsStore>
  );
};

MainLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  disclaimers: PropTypes.shape({
    drupal_id: PropTypes.string,
    field_text: PropTypes.arrayOf(PropTypes.string),
    internal: PropTypes.shape({
      type: PropTypes.string,
    }),
  }),
};

MainLayout.defaultProps = {
  disclaimers: null,
};
